import { useEffect, useState, useMemo } from "react";

import { FormattedMessage, FormattedDate } from "react-intl";

import Heading from "@atlaskit/heading";
import GlobeIcon from "@atlaskit/icon/core/globe";
import Link from "@atlaskit/link";
import {
    ConfluenceIcon,
    JiraIcon,
    JiraDataCenterIcon,
    ConfluenceDataCenterIcon
} from "@atlaskit/logo-next";
import Lozenge from "@atlaskit/lozenge-next";
import { Stack, Text, Inline, xcss, Box } from "@atlaskit/primitives-next";

import {
    TableHeads,
    TableRows
} from "@/components/fastshift/src/common/fastshift-table";
import { useBxpWebStargateClient } from "@/libraries/bxp-web-stargate-client/src";

import {
    DEFAULT_CONTROLS_PAYLOAD,
    Pagination,
    ControlsPayload
} from "../../../../common/fastshift-table";
import { SAC_URL } from "../../../../common/routes/constants";
import {
    getFastshiftSupportRequests,
    setMigrationStatusTableControls,
    useFastshiftDispatch,
    useFastshiftSelector
} from "../../../../state";
import {
    NO_ORG_FILTER,
    OTHER_ORG_FILTER
} from "../org-selector-dropdown/constants";
import { getStatusCategoryStyleMapping } from "../utils";

import { messages } from "./messages";
import { useDashboardUIAnalytics } from "./use-dashboard-ui-analytics";

const iconWrapperStyles = xcss({
    width: "20px",
    height: "20px",
    borderRadius: "5px",
    backgroundColor: "color.background.accent.gray.subtler"
});

export const useMigrationStatusTable = () => {
    const dispatch = useFastshiftDispatch();
    const stargateClient = useBxpWebStargateClient();
    const { sendMigrationProgressTableOpenSupportTicketClickUIEvent } =
        useDashboardUIAnalytics();

    const { migrationStatusTableControls } = useFastshiftSelector(
        (state) => state.pageStateCache
    );

    // TODO: Temp pagination and sortinguntil BE supports it
    const [pagination, setPagination] = useState<Pagination>({
        page: DEFAULT_CONTROLS_PAYLOAD.page,
        size: DEFAULT_CONTROLS_PAYLOAD.size,
        totalElements: 0,
        totalPages: 0,
        hasNext: false,
        hasPrevious: false,
        sorted: false
    });

    const { pending: customerDomainPending, data: customerDomainData } =
        useFastshiftSelector((state) => state.customerDomain);

    const selectedDomainOrg = useFastshiftSelector(
        (state) => state.selectedDomainOrg
    );

    const {
        data: supportRequestsData,
        pending: supportRequestsPending,
        error: supportRequestsError
    } = useFastshiftSelector((state) => state.supportRequests);

    useEffect(() => {
        const totalPages = Math.ceil(
            supportRequestsData.length / pagination.size
        );

        setPagination({
            ...pagination,
            totalElements: supportRequestsData.length,
            totalPages,
            hasNext: pagination.page < totalPages - 1,
            hasPrevious: pagination.page > 0
        });
    }, [supportRequestsData]);

    const onSetControlsPayload = (controls: ControlsPayload) => {
        const { page, size } = controls;
        const totalPages = Math.ceil(supportRequestsData.length / size);
        dispatch(
            setMigrationStatusTableControls({
                migrationStatusTableControls: controls
            })
        );

        setPagination({
            ...pagination,
            page,
            size,
            totalPages,
            hasNext: page < totalPages - 1,
            hasPrevious: page > 0
        });
    };

    const supportTicketQueryParams = useMemo(() => {
        if (!selectedDomainOrg) return {};
        switch (selectedDomainOrg.orgId) {
            case NO_ORG_FILTER.orgId:
                return {};
            case OTHER_ORG_FILTER.orgId:
                return { permissionLevel: "NON_ADMIN" as const };
            default:
                return {
                    orgId: selectedDomainOrg.orgId,
                    permissionLevel: "ADMIN" as const
                };
        }
    }, [selectedDomainOrg]);

    const handleRefresh = () => {
        if (customerDomainData && selectedDomainOrg) {
            dispatch(
                getFastshiftSupportRequests({
                    stargateClient,
                    customerDomainId: customerDomainData.customerDomainId,
                    ...supportTicketQueryParams
                })
            );
        }
    };

    useEffect(() => {
        let supportRequestsPromise = null;

        if (customerDomainData && selectedDomainOrg) {
            supportRequestsPromise = dispatch(
                getFastshiftSupportRequests({
                    stargateClient,
                    customerDomainId: customerDomainData.customerDomainId,
                    ...supportTicketQueryParams
                })
            );
        }

        return () => {
            if (supportRequestsPromise) {
                supportRequestsPromise.abort();
            }
        };
    }, [
        dispatch,
        stargateClient,
        customerDomainData,
        selectedDomainOrg,
        supportTicketQueryParams
    ]);

    const productIconMapping: Record<
        "source" | "destination",
        Record<string, React.ReactNode>
    > = {
        source: {
            JIRA: <JiraDataCenterIcon appearance="brand" size="xsmall" />,
            CONFLUENCE: (
                <ConfluenceDataCenterIcon appearance="brand" size="xsmall" />
            )
        },
        destination: {
            JIRA: (
                <JiraIcon
                    appearance="brand"
                    size="xsmall"
                    shouldUseNewLogoDesign
                />
            ),
            CONFLUENCE: (
                <ConfluenceIcon
                    appearance="brand"
                    size="xsmall"
                    shouldUseNewLogoDesign
                />
            )
        }
    };

    const productNameMapping: Record<string, React.ReactNode> = {
        JIRA: (
            <FormattedMessage
                {...messages.migrationStatusTableJiraProductName}
            />
        ),
        CONFLUENCE: (
            <FormattedMessage
                {...messages.migrationStatusTableConfluenceProductName}
            />
        )
    };

    const head: TableHeads = {
        cells: [
            {
                key: "source",
                content: (
                    <Heading size="xxsmall">
                        <FormattedMessage
                            {...messages.migrationStatusTableSourceColumnTitle}
                        />
                    </Heading>
                ),
                width: 24
            },
            {
                key: "destination",
                content: (
                    <Heading size="xxsmall">
                        <FormattedMessage
                            {...messages.migrationStatusTableDestinationColumnTitle}
                        />
                    </Heading>
                ),
                width: 24
            },
            {
                key: "phase",
                content: (
                    <Heading size="xxsmall">
                        <FormattedMessage
                            {...messages.migrationStatusTablePhaseColumnTitle}
                        />
                    </Heading>
                )
            },
            {
                key: "status",
                content: (
                    <Heading size="xxsmall">
                        <FormattedMessage
                            {...messages.migrationStatusTableStatusColumnTitle}
                        />
                    </Heading>
                )
            },
            {
                key: "ticket",
                content: (
                    <Heading size="xxsmall">
                        <FormattedMessage
                            {...messages.migrationStatusTableTicketColumnTitle}
                        />
                    </Heading>
                ),
                width: 15
            },
            {
                key: "planToMigrateDate",
                content: (
                    <Heading size="xxsmall">
                        <FormattedMessage
                            {...messages.migrationStatusTablePlanToMigrateDateColumnTitle}
                        />
                    </Heading>
                ),
                width: 15
            }
        ]
    };

    const rows: TableRows = supportRequestsData
        .slice(
            pagination.page * pagination.size,
            (pagination.page + 1) * pagination.size
        )
        .map((request) => {
            return {
                key: request.supportTicketId,
                cells: [
                    {
                        key: "source",
                        content: (
                            <Inline space="space.100">
                                <Inline
                                    alignBlock="center"
                                    alignInline="center"
                                    xcss={iconWrapperStyles}
                                >
                                    {(request.source.productName
                                        ? productIconMapping.source[
                                              request.source.productName
                                          ]
                                        : null) || (
                                        <GlobeIcon label="globe" size="small" />
                                    )}
                                </Inline>
                                <Stack>
                                    <Text size="medium">
                                        {(request.source.productName
                                            ? productNameMapping[
                                                  request.source.productName
                                              ]
                                            : null) || (
                                            <FormattedMessage
                                                {...messages.migrationStatusTableSourceDestinationColumnContentDefault}
                                            />
                                        )}
                                    </Text>
                                    <Text
                                        size="small"
                                        color="color.text.subtlest"
                                    >
                                        {request.source.baseUrl ? (
                                            request.source.baseUrl
                                        ) : (
                                            <FormattedMessage
                                                {...messages.migrationStatusTableSourceDestinationColumnContentNoBaseUrl}
                                            />
                                        )}
                                    </Text>
                                </Stack>
                            </Inline>
                        )
                    },
                    {
                        key: "destination",
                        content: (
                            <Inline space="space.100">
                                <Inline
                                    alignBlock="center"
                                    alignInline="center"
                                    xcss={iconWrapperStyles}
                                >
                                    {(request.destination.productName
                                        ? productIconMapping.destination[
                                              request.destination.productName
                                          ]
                                        : null) || (
                                        <GlobeIcon label="globe" size="small" />
                                    )}
                                </Inline>
                                <Stack>
                                    <Text size="medium">
                                        {(request.destination.productName
                                            ? productNameMapping[
                                                  request.destination
                                                      .productName
                                              ]
                                            : null) || (
                                            <FormattedMessage
                                                {...messages.migrationStatusTableSourceDestinationColumnContentDefault}
                                            />
                                        )}
                                    </Text>
                                    <Text
                                        size="small"
                                        color="color.text.subtlest"
                                    >
                                        {request.destination.baseUrl ? (
                                            request.destination.baseUrl
                                        ) : (
                                            <FormattedMessage
                                                {...messages.migrationStatusTableSourceDestinationColumnContentNoBaseUrl}
                                            />
                                        )}
                                    </Text>
                                </Stack>
                            </Inline>
                        )
                    },
                    {
                        key: "phase",
                        content: (
                            <Text size="medium">{request.migrationPhase}</Text>
                        )
                    },
                    {
                        key: "status",
                        content: (
                            <Box as="span">
                                <Lozenge
                                    appearance={getStatusCategoryStyleMapping(
                                        request.status
                                    )}
                                >
                                    {request.status}
                                </Lozenge>
                            </Box>
                        )
                    },
                    {
                        key: "ticket",
                        content: (
                            <Link
                                href={`${SAC_URL}/requests/${request.supportTicketId}`}
                                target="_blank"
                                onClick={() =>
                                    sendMigrationProgressTableOpenSupportTicketClickUIEvent(
                                        request.source.senId,
                                        request.supportTicketId
                                    )
                                }
                            >
                                {request.supportTicketId}
                            </Link>
                        )
                    },
                    {
                        key: "planToMigrateDate",
                        content: (
                            <Text size="medium">
                                {request.planToMigrateDate ? (
                                    <FormattedDate
                                        value={
                                            new Date(request.planToMigrateDate)
                                        }
                                        day="2-digit"
                                        month="short"
                                        year="numeric"
                                    />
                                ) : (
                                    <FormattedMessage
                                        {...messages.migrationStatusTablePlanToMigrateDateColumnContentDefault}
                                    />
                                )}
                            </Text>
                        )
                    }
                ]
            };
        });

    return {
        isTableLoading: customerDomainPending || supportRequestsPending,
        isTableError: !!supportRequestsError,
        rows,
        head,
        controls: {
            pagination,
            onSetControlsPayload,
            controls: migrationStatusTableControls
        },
        handleRefresh
    };
};
